/**
 * Inaugural 90-Day Protocol campaign resolver.
 *
 * Pure + isomorphic: the same function decides what the product page shows,
 * what the cart charges and what the server actually bills at payment-order
 * creation. Campaign rows come from `public.promo_campaigns` (admin-managed),
 * so an offer can be pulled instantly without a deploy.
 *
 * Rules encoded here:
 *   - A campaign applies only while `active` and `starts_at <= now <= expires_at`.
 *   - Exactly `qualifying_qty` units form one protocol block.
 *   - At most `max_per_order` blocks per campaign per order (six bottles do
 *     NOT become two protocols).
 *   - The block price REPLACES the normal ladder price for those units; the
 *     remaining units fall back to normal pack/bundle pricing.
 */
import { inferSkuHandle } from "@/lib/checkout-pricing";

export type PromoCampaign = {
  id: string;
  name: string;
  productHandle: string;
  campaignType: string;
  qualifyingQty: number;
  offerPricePaise: number;
  prepaidDiscountBps: number;
  maxPerOrder: number;
  startsAt: string;
  expiresAt: string;
  timezone: string;
  active: boolean;
};

export type PromoCampaignRow = {
  id: string;
  name: string;
  product_handle: string;
  campaign_type: string;
  qualifying_qty: number;
  offer_price_paise: number;
  prepaid_discount_bps: number;
  max_per_order: number;
  starts_at: string;
  expires_at: string;
  timezone: string;
  active: boolean;
};

export function campaignFromRow(row: PromoCampaignRow): PromoCampaign {
  return {
    id: row.id,
    name: row.name,
    productHandle: row.product_handle,
    campaignType: row.campaign_type,
    qualifyingQty: Number(row.qualifying_qty) || 3,
    offerPricePaise: Number(row.offer_price_paise) || 0,
    prepaidDiscountBps: Number(row.prepaid_discount_bps) || 0,
    maxPerOrder: Math.max(1, Number(row.max_per_order) || 1),
    startsAt: row.starts_at,
    expiresAt: row.expires_at,
    timezone: row.timezone || "Asia/Kolkata",
    active: !!row.active,
  };
}

export type CampaignState = "live" | "scheduled" | "expired" | "inactive";

export function campaignState(c: PromoCampaign, now: Date = new Date()): CampaignState {
  if (!c.active || c.offerPricePaise <= 0 || c.qualifyingQty < 1) return "inactive";
  const t = now.getTime();
  if (t < Date.parse(c.startsAt)) return "scheduled";
  if (t > Date.parse(c.expiresAt)) return "expired";
  return "live";
}

export function isCampaignLive(c: PromoCampaign, now: Date = new Date()): boolean {
  return campaignState(c, now) === "live";
}

/** The live campaign for a product handle, if any. */
export function findLiveCampaign(
  campaigns: PromoCampaign[] | null | undefined,
  handle: string | null | undefined,
  now: Date = new Date(),
): PromoCampaign | null {
  if (!campaigns?.length || !handle) return null;
  const target = inferSkuHandle({ handle }) ?? handle;
  return (
    campaigns.find(
      (c) => c.productHandle === target && isCampaignLive(c, now),
    ) ?? null
  );
}

export type PromoLine = {
  key: string;
  handle?: string | undefined;
  title?: string | undefined;
  unitPricePaise: number;
  quantity: number;
};

export type InauguralApplied = {
  campaignId: string;
  name: string;
  handle: string;
  /** How many protocol blocks were applied (capped by maxPerOrder). */
  blocks: number;
  /** Units consumed by the campaign = blocks × qualifyingQty. */
  quantity: number;
  offerPricePaise: number;
  /** What those units would have cost at the regular single-bottle price. */
  regularPricePaise: number;
  savingsPaise: number;
};

export type InauguralResult = {
  discountPaise: number;
  applied: InauguralApplied[];
  /** Units already priced by the campaign, keyed by cart-line key. */
  consumedByKey: Record<string, number>;
};

const EMPTY: InauguralResult = { discountPaise: 0, applied: [], consumedByKey: {} };

/**
 * Apply inaugural protocol pricing to cart lines. Runs BEFORE bundle/tiered
 * pricing so campaign units are never discounted twice.
 */
export function computeInauguralDiscounts(
  lines: PromoLine[],
  campaigns: PromoCampaign[] | null | undefined,
  now: Date = new Date(),
): InauguralResult {
  if (!campaigns?.length || !lines.length) return EMPTY;

  const applied: InauguralApplied[] = [];
  const consumedByKey: Record<string, number> = {};
  let total = 0;

  for (const campaign of campaigns) {
    if (!isCampaignLive(campaign, now)) continue;

    const matching = lines.filter(
      (l) =>
        inferSkuHandle({ handle: l.handle, title: l.title }) ===
        campaign.productHandle,
    );
    if (!matching.length) continue;

    let blocks = 0;
    let quantity = 0;
    let regularPricePaise = 0;
    let savings = 0;

    for (const line of matching) {
      let free = line.quantity - (consumedByKey[line.key] ?? 0);
      while (blocks < campaign.maxPerOrder && free >= campaign.qualifyingQty) {
        const regular = line.unitPricePaise * campaign.qualifyingQty;
        const blockSavings = regular - campaign.offerPricePaise;
        if (blockSavings <= 0) {
          free = 0;
          break;
        }
        blocks += 1;
        quantity += campaign.qualifyingQty;
        regularPricePaise += regular;
        savings += blockSavings;
        free -= campaign.qualifyingQty;
        consumedByKey[line.key] =
          (consumedByKey[line.key] ?? 0) + campaign.qualifyingQty;
      }
      if (blocks >= campaign.maxPerOrder) break;
    }

    if (blocks > 0) {
      total += savings;
      applied.push({
        campaignId: campaign.id,
        name: campaign.name,
        handle: campaign.productHandle,
        blocks,
        quantity,
        offerPricePaise: campaign.offerPricePaise * blocks,
        regularPricePaise,
        savingsPaise: savings,
      });
    }
  }

  return { discountPaise: total, applied, consumedByKey };
}

/**
 * Process-wide registry of live campaigns, hydrated once per request on the
 * server and once on mount in the browser. Lets shared pricing code
 * (`computeCartDiscounts`) stay campaign-aware without threading the rows
 * through every call site.
 */
let ACTIVE_CAMPAIGNS: PromoCampaign[] = [];

export function setActiveCampaigns(campaigns: PromoCampaign[]): void {
  ACTIVE_CAMPAIGNS = campaigns;
}

export function getActiveCampaigns(): PromoCampaign[] {
  return ACTIVE_CAMPAIGNS;
}

/** Milliseconds left before the campaign ends (0 when not live). */
export function msUntilExpiry(c: PromoCampaign, now: Date = new Date()): number {
  return Math.max(0, Date.parse(c.expiresAt) - now.getTime());
}

export const FINAL_COUNTDOWN_MS = 72 * 60 * 60 * 1000;

/** Only show a live countdown inside the last 72 hours. */
export function shouldShowCountdown(c: PromoCampaign, now: Date = new Date()): boolean {
  const left = msUntilExpiry(c, now);
  return left > 0 && left <= FINAL_COUNTDOWN_MS;
}

/** "20 Sep" in the campaign's own timezone. */
export function formatEndDateShort(c: PromoCampaign): string {
  try {
    return new Intl.DateTimeFormat("en-IN", {
      day: "numeric",
      month: "short",
      timeZone: c.timezone,
    }).format(new Date(c.expiresAt));
  } catch {
    return new Date(c.expiresAt).toDateString();
  }
}

/** "20 September 2026, 11:59:59 PM IST" style long form. */
export function formatEndDateLong(c: PromoCampaign): string {
  try {
    const d = new Intl.DateTimeFormat("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
      timeZone: c.timezone,
    }).format(new Date(c.expiresAt));
    return `${d} IST`;
  } catch {
    return new Date(c.expiresAt).toISOString();
  }
}
